import {
	emailHref,
	MAIN_SITE_LINKS,
	mainSiteUrl,
	mapHref,
	ORGANIZATION,
	PRODUCT,
	phoneHref,
} from "@nwga/contracts/organization";
import { COUNTIES, REGION_NAME } from "@nwga/contracts/region";
import { Link } from "@tanstack/react-router";
import { Accessibility, Mail, MapPin, Phone, Printer } from "lucide-react";
import { Container } from "./container.tsx";
import { JdaWordmark } from "./logo.tsx";
import { SocialIcon, type SocialName } from "./social-icon.tsx";

/**
 * The footer.
 *
 * Deliberately the same navy band the main site closes with, carrying the same
 * address, phone and social accounts — the two sites should end identically
 * even though the columns between them differ.
 *
 * The county links stay because "jobs in Walker County" is a real page with its
 * own canonical URL and a search people actually make; internal links are how
 * it gets found.
 */
const SECTIONS = [
	{
		heading: "Find work",
		links: [{ to: "/jobs", label: "All jobs" }],
	},
	{
		heading: "For employers",
		links: [
			{ to: "/businesses/register", label: "Register a business" },
			{ to: "/dashboard", label: "Your dashboard" },
		],
	},
	{
		heading: "This site",
		links: [
			{ to: "/about", label: "About" },
			{ to: "/contact", label: "Contact" },
			{ to: "/terms", label: "Terms" },
			{ to: "/privacy", label: "Privacy" },
		],
	},
] as const;

const SOCIALS: readonly { label: string; href: string; name: SocialName }[] = [
	{ label: "LinkedIn", href: ORGANIZATION.social.linkedin, name: "linkedin" },
	{ label: "Facebook", href: ORGANIZATION.social.facebook, name: "facebook" },
	{
		label: "Instagram",
		href: ORGANIZATION.social.instagram,
		name: "instagram",
	},
];

export function AppFooter() {
	const year = new Date().getFullYear();

	return (
		// No top margin: pages carry their own closing padding, and a margin here
		// opens a strip of page background between a full-bleed final section and
		// the footer — invisible on white, an obvious seam in dark mode.
		<footer className="bg-band text-band-foreground">
			<Container className="py-14">
				<div className="grid gap-10 lg:grid-cols-12">
					<div className="lg:col-span-4">
						<a
							href={ORGANIZATION.url}
							target="_blank"
							rel="noreferrer"
							aria-label={`${ORGANIZATION.name} (opens in new tab)`}
							className="inline-flex"
						>
							<JdaWordmark className="h-12 w-auto text-band-foreground" />
						</a>

						<p className="mt-5 max-w-sm text-sm leading-relaxed text-band-foreground/75">
							{PRODUCT.name} is where employers across {REGION_NAME} post
							openings and manage their business profile — a service of the
							Joint Development Authority, free for every employer in Catoosa,
							Chattooga, Dade and Walker counties.
						</p>

						<address className="mt-6 space-y-2.5 text-sm text-band-foreground/85 not-italic">
							<a
								href={mapHref}
								target="_blank"
								rel="noreferrer"
								className="flex items-start gap-2.5 hover:text-band-foreground"
							>
								<MapPin
									className="mt-0.5 size-4 shrink-0 text-brand"
									aria-hidden="true"
								/>
								<span>
									{ORGANIZATION.contact.address}
									<br />
									{ORGANIZATION.contact.city}, {ORGANIZATION.contact.state}{" "}
									{ORGANIZATION.contact.zip}
								</span>
								<span className="sr-only">(opens in new tab)</span>
							</a>
							<a
								href={phoneHref}
								className="flex items-center gap-2.5 hover:text-band-foreground"
							>
								<Phone
									className="size-4 shrink-0 text-brand"
									aria-hidden="true"
								/>
								{ORGANIZATION.contact.phone}
							</a>
							<p className="flex items-center gap-2.5">
								<Printer
									className="size-4 shrink-0 text-brand"
									aria-hidden="true"
								/>
								{ORGANIZATION.contact.fax}
								<span className="text-band-foreground/55">(fax)</span>
							</p>
							<a
								href={emailHref}
								className="flex items-center gap-2.5 hover:text-band-foreground"
							>
								<Mail
									className="size-4 shrink-0 text-brand"
									aria-hidden="true"
								/>
								{ORGANIZATION.contact.email}
							</a>
						</address>

						<div className="mt-6 flex gap-2.5">
							{SOCIALS.map((social) => (
								<a
									key={social.name}
									href={social.href}
									target="_blank"
									rel="noreferrer"
									aria-label={`${social.label} (opens in new tab)`}
									className="grid size-9 place-items-center rounded-sm bg-band-foreground/10 text-band-foreground/90 transition-colors hover:bg-brand hover:text-brand-foreground"
								>
									<SocialIcon name={social.name} className="size-4" />
								</a>
							))}
						</div>
					</div>

					<div className="lg:col-span-8">
						<nav
							aria-label="Footer"
							className="grid grid-cols-2 gap-8 sm:grid-cols-4"
						>
							{SECTIONS.map((section) => (
								<div key={section.heading}>
									<h2 className="font-heading text-sm font-semibold text-band-foreground">
										{section.heading}
									</h2>
									<ul className="mt-3 space-y-2 text-sm text-band-foreground/75">
										{section.links.map((link) => (
											<li key={link.to}>
												<Link
													to={link.to}
													className="transition-colors hover:text-brand"
												>
													{link.label}
												</Link>
											</li>
										))}
									</ul>
								</div>
							))}

							<div>
								<h2 className="font-heading text-sm font-semibold text-band-foreground">
									<a
										href={ORGANIZATION.url}
										target="_blank"
										rel="noreferrer"
										className="hover:text-brand"
									>
										{ORGANIZATION.abbr}
										<span className="sr-only"> (opens in new tab)</span>
									</a>
								</h2>
								<ul className="mt-3 space-y-2 text-sm text-band-foreground/75">
									{MAIN_SITE_LINKS.map((link) => (
										<li key={link.href}>
											<a
												href={mainSiteUrl(link.href)}
												target="_blank"
												rel="noreferrer"
												className="transition-colors hover:text-brand"
											>
												{link.label}
												<span className="sr-only"> (opens in new tab)</span>
											</a>
										</li>
									))}
								</ul>
							</div>
						</nav>

						<div className="mt-10 border-t border-band-foreground/10 pt-8">
							<nav aria-label="Browse by county">
								<h2 className="font-heading text-sm font-semibold text-band-foreground">
									Browse by county
								</h2>
								<ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-band-foreground/75">
									{COUNTIES.map((county) => (
										<li key={county.slug}>
											<Link
												to="/jobs"
												search={{ county: county.slug }}
												className="transition-colors hover:text-brand"
											>
												Jobs in {county.name} County
											</Link>
										</li>
									))}
								</ul>
							</nav>
						</div>
					</div>
				</div>
			</Container>

			<div className="border-t border-band-foreground/10">
				<Container className="flex flex-col items-center justify-between gap-3 py-5 text-xs text-band-foreground/65 sm:flex-row">
					<p>
						© {year} {ORGANIZATION.name}. All rights reserved.
					</p>
					{/* The authority publishes one accessibility statement, on the main
              site, and it covers both properties. */}
					<a
						href={mainSiteUrl("/accessibility")}
						target="_blank"
						rel="noreferrer"
						className="flex items-center gap-1.5 rounded-full border border-band-foreground/15 px-3 py-1 transition-colors hover:border-brand hover:text-brand"
					>
						<Accessibility
							className="size-3.5 shrink-0 text-brand"
							aria-hidden="true"
						/>
						WCAG 2.1 AA
						<span className="sr-only"> (opens in new tab)</span>
					</a>
					<p>
						Website made by{" "}
						<a
							href="https://lemonyclick.com/"
							target="_blank"
							rel="noreferrer"
							className="text-band-foreground/85 underline-offset-2 transition-colors hover:text-brand hover:underline"
						>
							Lemony Click
							<span className="sr-only"> (opens in new tab)</span>
						</a>
						.
					</p>
				</Container>
			</div>
		</footer>
	);
}
